//dependencies
import React from "react";

import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";

//hooks
import { useInputId } from "../../../hooks/useInputId";

import Container from "../../Container";
import Icon from "../../Icon";
//components
import InputWrapper from "../InputWrapper";
//css
import css from "./RangeInput.module.scss";

type RangeInputProps = {
	/**
	 * The value of the input
	 */
	value: number;
	/**
	 * The function to call when the input value changes
	 */
	onChange: (value: number) => void;
	/**
	 * The minimum value of the input
	 */
	min: number;
	/**
	 * The maximum value of the input
	 */
	max: number;
	/**
	 * The step value of the input
	 */
	step: number;
	/**
	 * The label of the input
	 */
	label?: string;
	/**
	 * The error message of the input
	 */
	errorMessage?: string;
	/**
	 * The class name of the input
	 */
	className?: string;

	/**
	 * testid
	 */
	testid?: string;
};

const RangeInput = ({
	value,
	onChange,
	min,
	max,
	step,
	label,
	errorMessage,
	className = "",
	testid = "range-input",
}: RangeInputProps) => {
	const inputRef = React.useRef<HTMLInputElement>(null);
	const inputId = useInputId();
	const percentage = ((value - min) / (max - min)) * 100;

	return (
		<InputWrapper label={label} errorMessage={errorMessage} inputId={inputId}>
			<Container className={`${css.rangeInputContainer} ${className}`} gap="8px" verticalAlignment="center">
				<Icon size={24} name="minus" className={css.rangeIcon} icon={faMinus} />
				<div className={css.rangeWrapper}>
					<div className={css.rangeTrack} />
					<div className={css.rangeProgress} style={{ width: `${percentage}%` }} />
					<div
						className={`${css.thumb} ${document.activeElement === inputRef.current ? css.focused : ""}`}
						style={{ left: `max(calc(${percentage}% - 18px), 0px)` }}
						onClick={() => inputRef.current?.focus()}
					/>
					<input
						ref={inputRef}
						id={inputId}
						data-testid={testid}
						type="range"
						min={min}
						max={max}
						step={step}
						value={value}
						onChange={(e) => onChange(parseFloat(e.target.value))}
						onFocus={() => inputRef.current?.focus()}
						className={`${css.rangeInput}`}
					/>
				</div>
				<Icon size={24} name="plus" className={css.rangeIcon} icon={faPlus} />
			</Container>
		</InputWrapper>
	);
};

export default RangeInput;
